import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import pillarsLogo from "../assets/logo/pillars.svg";
import type { Player, Session } from "../api/models";
import { Dictation, type DictationState } from "../dictation/dictation";
import { useFloorGames } from "../floor/api";
import { placed, type Point } from "../floor/floor-logic";
import { ProgressSpinner } from "../host-activity/ProgressSpinner";
import { NotesPrefsSidebar } from "../players/sidebars/NotesPrefsSidebar";
import { usePreferences } from "../players/profile-api";
import { InputGroup } from "../ui/InputGroup";
import { PButton } from "../ui/PButton";
import { PSidebar } from "../ui/PSidebar";
import { useToast } from "../ui/toast";
import { getActiveSession, getPatronDetail, healthProbe, pillarsAssistAi, sendPrompt, usePillarsAssistAvailable } from "./api";
import {
  appendTranscript,
  DICTATING_TOAST,
  ERROR_RESPONSE,
  hasSession,
  keydownAction,
  mergePatronDetail,
  promptRequest,
  promptResult,
  pushPrompt,
  sendDisabled,
  sessionRequest,
  submitDecision,
  UNAVAILABLE_RESPONSE,
  type CurrentPatron,
  type HistoryItem,
} from "./assist-logic";
import { PatronDetailSidebar, PatronLocationSidebar } from "./PatronSidebars";
import { MicControls, OverlayPanel, PatronSelect, Pi, PromptInput, PromptNotices, SendAddon } from "./widgets";
import "./pillars-assist.css";

// `InPlay` POLLS THE SELECTED SESSION EVERY 15S; SO DOES THIS PANEL'S LOCATION VIEW
const SESSION_POLL_MS = 15_000;
// `clearSelectedSession` WAITS OUT THE MAP'S 1.5S ZOOM-OUT BEFORE THE SIDEBAR CLOSES
const ZOOM_OUT_MS = 1500;

// PORT OF `<pillars-assist />`: THE TOP-BAR LOGO IS ITS TRIGGER. PRESSING IT TOGGLES A SMALL
// "PILLARS ASSIST:" PANEL WITH THE PROMPT FIELD; SENDING OPENS THE LEFT CONVERSATION SIDEBAR,
// WHERE EACH ANSWER OFFERS THE PLAYERS FOUND AND, FOR THE ONE PICKED, ITS LOCATION, DETAIL,
// NOTES AND PREFERENCES. PROMPTS GO TO `/pillars-assist/send-prompt` ON THE CORE API ONLY, UNDER
// THE "pillars-assist" AVAILABILITY CHANNEL. ONE RECORDER SERVES BOTH PROMPT FIELDS, AS THE
// COMPONENT-SCOPED `SpeechToTextService` DOES.
export function PillarsAssist() {
  const toast = useToast();
  const navigate = useNavigate();
  const aiAvailable = usePillarsAssistAvailable();

  // TRIGGER + OVERLAY
  const triggerRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // PROMPT + DICTATION
  const [prompt, setPrompt] = useState("");
  const promptRef = useRef(prompt);
  promptRef.current = prompt;
  const [speech, setSpeech] = useState<DictationState | null>(null);
  const dictation = useRef<Dictation | null>(null);
  useEffect(() => {
    const d = new Dictation(setSpeech, (text) => setPrompt((p) => appendTranscript(p, text)));
    dictation.current = d;
    setSpeech(d.current);
    return () => d.dispose();
  }, []);

  // CONVERSATION
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const historyRef = useRef(history);
  historyRef.current = history;
  const nextId = useRef(0);
  const patch = useCallback((id: number, p: Partial<HistoryItem>) => setHistory((h) => h.map((x) => (x.id === id ? { ...x, ...p } : x))), []);

  // A COMPONENT-LIFETIME ABORT, AS ANGULAR'S `destroy$` TEARDOWN
  const lifetime = useRef(new AbortController());
  useEffect(() => {
    const abort = new AbortController();
    lifetime.current = abort;
    return () => abort.abort();
  }, []);

  // PATRON + ACTION SIDEBARS
  const [currentPatron, setCurrentPatron] = useState<CurrentPatron | null>(null);
  const currentPatronRef = useRef(currentPatron);
  currentPatronRef.current = currentPatron;
  const detailRequest = useRef(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // LOCATION VIEW
  const games = useFloorGames();
  const [uiBlocked, setUiBlockedState] = useState(false);
  const uiBlockedRef = useRef(false);
  const setUiBlocked = (v: boolean) => {
    uiBlockedRef.current = v;
    setUiBlockedState(v);
  };
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [currentGame, setCurrentGame] = useState<Point | null>(null);
  const [zoomedIn, setZoomedIn] = useState(false);
  const [pulse, setPulse] = useState<Point | null>(null);
  const [zoomTo, setZoomTo] = useState<Point | null>(null);
  const closeTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  // CLOSING EITHER PROMPT SURFACE ABANDONS DICTATION IN FLIGHT RATHER THAN TRANSCRIBING IT INTO A
  // PANEL THE USER JUST DISMISSED
  const stopListening = useCallback(() => {
    dictation.current?.cancel();
    setEnabled(false);
  }, []);

  const hideOverlay = useCallback(() => {
    setOverlayOpen(false);
    stopListening();
  }, [stopListening]);

  // `initPillarsAssist(); op.toggle($event)`
  const onTrigger = () => {
    if (overlayOpen) {
      setPrompt("");
      hideOverlay();
    } else {
      setEnabled(true);
      setOverlayOpen(true);
    }
  };

  // `processPrompt`: HEALTH IS THE SOURCE OF TRUTH FOR AVAILABILITY - A MISCONFIGURED AI BACKEND
  // CAN ANSWER SEND-PROMPT WITH A 400 - SO A FAILURE IS SHOWN AT ONCE AND THEN PROBED
  const processPrompt = async (item: HistoryItem) => {
    setThinking(true);
    const signal = lifetime.current.signal;
    const outcome = await pillarsAssistAi.guardedCall(
      () => sendPrompt(promptRequest(item), signal),
      healthProbe,
      signal,
      () => {
        if (signal.aborted) return;
        setThinking(false);
        patch(item.id, { isThinking: false, response: ERROR_RESPONSE });
      },
    );
    if (signal.aborted) return;
    switch (outcome.status) {
      case "success":
        setThinking(false);
        patch(item.id, { isThinking: false, ...promptResult(outcome.value) });
        break;
      case "unavailable":
        patch(item.id, { response: UNAVAILABLE_RESPONSE });
        break;
      case "cancelled":
        setThinking(false);
        patch(item.id, { isThinking: false });
        break;
      default:
        break;
    }
  };

  const submitPrompt = () => {
    const decision = submitDecision({ aiAvailable: pillarsAssistAi.isAvailable, prompt: promptRef.current, dictationBusy: dictation.current?.current.isBusy ?? false });
    if (decision === "ignore") return;
    if (decision === "dictating") {
      toast("warning", DICTATING_TOAST.title, DICTATING_TOAST.detail);
      return;
    }
    const next = pushPrompt(historyRef.current, promptRef.current, ++nextId.current);
    historyRef.current = next;
    setHistory(next);
    setThinking(true);
    setPrompt("");
    hideOverlay();
    setSidebarOpen(true);
    void processPrompt(next[0]!);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const action = keydownAction(e.key, e.shiftKey);
    if (!action) return;
    if (action === "newline") setPrompt((p) => p + "\n");
    else submitPrompt();
    e.preventDefault();
  };

  // `getPatronDetail`. ONLY THE LATEST PICK MAY SET `currentPatron`
  const pickPatron = async (item: HistoryItem, index: number) => {
    const patron = item.patrons?.[index];
    if (!patron) return;
    const request = ++detailRequest.current;
    patch(item.id, { selectedPatron: index, isLoading: true });
    setCurrentPatron(null);
    try {
      const resp = await getPatronDetail(patron.patronId);
      if (request !== detailRequest.current) return;
      const merged = mergePatronDetail(resp, patron);
      if (merged) setCurrentPatron(merged);
      else toast("error", "Error", "Patron detail not found.");
    } catch {
      if (request !== detailRequest.current) return;
      toast("error", "Error", "An error occurred while fetching patron detail.");
    } finally {
      patch(item.id, { isLoading: false });
    }
  };

  // `player-notes-prefs [showPreferenceAlert]="true"`: THE PICKED PLAYER'S ACTIONABLE PREFERENCES,
  // BY PRIORITY, AS STICKY INFO TOASTS, ONCE PER PICK
  const prefs = usePreferences(currentPatron?.patronId);
  const alertedFor = useRef<CurrentPatron | null>(null);
  useEffect(() => {
    if (!currentPatron || !prefs.data || alertedFor.current === currentPatron) return;
    alertedFor.current = currentPatron;
    for (const p of prefs.data.filter((x) => x.actionable === true)) {
      toast("info", p.patronPreferenceCategoryName ?? "", p.value ?? "", { life: 3000, sticky: true });
    }
  }, [currentPatron, prefs.data, toast]);

  // LOCATION -----------------------------------------------------------------------

  const clearSelectedSession = () => {
    setCurrentGame(null);
    setSelectedSession(null);
    setZoomedIn(false);
    setPulse(null);
    setZoomTo(null);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    // A DELIBERATE, VISIBLE DELAY: THE MAP FINISHES ZOOMING OUT BEFORE THE SIDEBAR GOES
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setUiBlocked(false);
      setLocationOpen(false);
    }, ZOOM_OUT_MS);
  };

  const sessionEnded = () => {
    toast("error", "Error", "The session is not longer active.");
    setUiBlocked(false);
    clearSelectedSession();
  };

  // `getSession`: ZOOMING IN BLOCKS THE UI UNTIL THE SESSION ANSWERS; A POLL DOES NOT, AND IS
  // SKIPPED WHILE THE UI IS BLOCKED
  const loadSession = async (patron: CurrentPatron | null, zoomIn: boolean) => {
    if (!hasSession(patron)) {
      setUiBlocked(false);
      clearSelectedSession();
      return;
    }
    if (uiBlockedRef.current) return;
    if (zoomIn) setUiBlocked(true);
    try {
      const resp = await getActiveSession(sessionRequest(patron));
      if (lifetime.current.signal.aborted) return;
      if (!resp.data) {
        sessionEnded();
        return;
      }
      setSelectedSession(resp.data);
      if (zoomIn) {
        // `ZoomToGameByLocation`: PULSE AND ZOOM ON THE MACHINE, WHEN THE FLOOR KNOWS IT
        setZoomedIn(true);
        const list = games.data ?? [];
        const game = list.find((g) => g.location === patron.location);
        const at = game && placed(game) ? { x: game.x, y: game.y } : null;
        setCurrentGame(at);
        if (at) {
          setPulse(at);
          setZoomTo(at);
        }
      }
      setUiBlocked(false);
    } catch {
      if (lifetime.current.signal.aborted) return;
      sessionEnded();
    }
  };
  const loadSessionRef = useRef(loadSession);
  loadSessionRef.current = loadSession;

  // POLL THE SESSION WHILE THE LOCATION VIEW IS OPEN
  useEffect(() => {
    if (!locationOpen) return;
    const id = window.setInterval(() => {
      if (currentPatronRef.current) void loadSessionRef.current(currentPatronRef.current, false);
    }, SESSION_POLL_MS);
    return () => window.clearInterval(id);
  }, [locationOpen]);

  const viewLocation = () => {
    setUiBlocked(false);
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setLocationOpen(true);
    void loadSession(currentPatron, true);
  };

  const mapZoomIn = () => {
    if (!currentGame) return;
    setZoomedIn(true);
    setZoomTo(currentGame);
  };
  const mapZoomOut = () => {
    setZoomedIn(false);
    setZoomTo(null);
  };

  // RENDER ---------------------------------------------------------------------------

  const disabled = sendDisabled({ prompt, aiAvailable, dictationBusy: speech?.isBusy ?? false });
  const mic = (
    <MicControls state={speech} onStart={() => void dictation.current?.start()} onStop={() => dictation.current?.stop()} />
  );
  const notices = <PromptNotices state={speech} aiAvailable={aiAvailable} onAllowMic={() => void dictation.current?.requestPermission()} />;

  return (
    <>
      {/* THE TRIGGER: THE LOGO, PULSING WHILE THE PANEL IS OPEN */}
      <div ref={triggerRef} className="cursor-pointer pillars-assist-trigger" onClick={onTrigger}>
        <img src={pillarsLogo} alt="logo" className={enabled ? "pillars-assist-pulse-animation" : ""} />
      </div>

      <OverlayPanel open={overlayOpen} anchor={triggerRef.current} onHide={hideOverlay}>
        <div className="flex flex-row justify-content-between align-items-center">
          <div className="font-bold">Pillars Assist:</div>
          <div>{mic}</div>
        </div>
        <div className="mt-3 p-fluid">
          <InputGroup className="pillars-assist-group">
            <PromptInput value={prompt} onChange={setPrompt} onKeyDown={onKeyDown} />
            <SendAddon disabled={disabled} onSend={submitPrompt} />
          </InputGroup>
          {notices}
        </div>
      </OverlayPanel>

      {/* THE CONVERSATION */}
      <PSidebar open={sidebarOpen} onClose={() => { setSidebarOpen(false); stopListening(); }} position="left" modal={false} width={{ xs: "100%", md: "30rem" }} ariaLabel="Pillars Assist">
        <div className="pillars-assist-inherit">
          <div className="mt-2 flex flex-row justify-content-center align-items-center">
            <img src={pillarsLogo} alt="logo" className={thinking ? "pillars-assist-pulse-animation" : ""} />
            <div className="ml-2 text-2xl font-bold">Pillars Assist</div>
          </div>

          {!thinking && (
            <div className="mt-3 p-fluid">
              <InputGroup className="pillars-assist-group">
                <div className="pillars-assist-addon">{mic}</div>
                <PromptInput value={prompt} onChange={setPrompt} onKeyDown={onKeyDown} />
                <SendAddon disabled={disabled} onSend={submitPrompt} />
              </InputGroup>
              {notices}
            </div>
          )}

          <div className="mt-3 conversation-container">
            {history.map((item, i) => (
              <div key={item.id} className="flex flex-column gap-2">
                <div className={`p-card p-component${i === 0 ? " surface-ground" : ""}`}>
                  <div className="p-card-body">
                    <div className="p-card-content">
                      <div className="flex align-items-start">
                        <Pi name="user" className="mr-2 mt-1" />
                        <div>
                          <div className="font-bold mb-1">You</div>
                          <div>{item.prompt}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`mt-2 p-card p-component${i === 0 ? " surface-ground" : ""}`}>
                  <div className="p-card-body">
                    <div className="p-card-content">
                      <div className="w-full">
                        <img src={pillarsLogo} alt="logo" className="mr-2 mt-1" style={{ width: 18, height: 18 }} />
                        <div>
                          <div className="font-bold mb-1">Pillars Assist</div>
                          {item.response && !item.isThinking && (
                            <div>
                              {item.isLoading ? (
                                <div className="flex align-items-center justify-content-center">
                                  <Pi name="spinner" spin size="3rem" className="text-gray-300" />
                                </div>
                              ) : (
                                <div className="fadein animation-duration-1000">
                                  {item.patrons && item.patrons.length > 0 && (
                                    <div className="mb-2 flex flex-column align-items-center justify-content-between gap-3">
                                      <div className="w-full">
                                        <PatronSelect patrons={item.patrons} value={item.selectedPatron} onChange={(index) => void pickPatron(item, index)} />
                                      </div>
                                      <div className="w-full flex flex-row align-items-center justify-content-end gap-2">
                                        {currentPatron?.location && (
                                          <PButton icon="map-marker" hostElement ariaLabel="Player location" disabled={!currentPatron} onClick={viewLocation} />
                                        )}
                                        <PButton icon="user" hostElement ariaLabel="Player detail" disabled={!currentPatron} onClick={() => setDetailOpen((v) => !v)} />
                                        <PButton icon="clipboard" hostElement ariaLabel="Player notes" disabled={!currentPatron} onClick={() => setNotesOpen((v) => !v)} />
                                        <PButton icon={<Pi name="tags" />} hostElement ariaLabel="Player preferences" disabled={!currentPatron} onClick={() => setPrefsOpen((v) => !v)} />
                                      </div>
                                    </div>
                                  )}
                                  {item.response}
                                </div>
                              )}
                            </div>
                          )}
                          {!item.response && item.isThinking && <div className="fadein animation-duration-1000 animation-iteration-infinite">Thinking...</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {i < history.length - 1 && (
                  <div className="conversation-divider my-3">
                    <hr className="border-top-1 border-gray-300 opacity-50" />
                  </div>
                )}
              </div>
            ))}

            {history.length === 0 && (
              <div className="flex flex-column align-items-center justify-content-center p-5">
                <Pi name="comments" size="3rem" className="text-gray-400 mt-3 mb-1" />
                <div className="text-gray-500">No conversation history yet. Ask a question to get started.</div>
              </div>
            )}
          </div>
        </div>
      </PSidebar>

      <PatronDetailSidebar
        open={detailOpen}
        patron={currentPatron}
        onClose={() => setDetailOpen(false)}
        onProfile={() => {
          if (currentPatron) void navigate({ to: "/players/$playerId", params: { playerId: currentPatron.cardId ?? "" } });
        }}
      />

      <PatronLocationSidebar
        open={locationOpen}
        patron={currentPatron}
        session={selectedSession}
        zoomedIn={zoomedIn}
        pulse={pulse}
        zoomTo={zoomTo}
        onZoomIn={mapZoomIn}
        onZoomOut={mapZoomOut}
        onCancel={clearSelectedSession}
      />

      {currentPatron && (
        <>
          <NotesPrefsSidebar player={currentPatron as unknown as Player} open={notesOpen} onClose={() => setNotesOpen(false)} dismissible={false} modal={false} />
          <NotesPrefsSidebar player={currentPatron as unknown as Player} open={prefsOpen} onClose={() => setPrefsOpen(false)} dismissible={false} modal={false} initialTab={1} />
        </>
      )}

      {uiBlocked && (
        <div className="pillars-assist-blockui" aria-busy="true">
          <ProgressSpinner />
        </div>
      )}
    </>
  );
}
